import React from "react";
import { Navigate, Route, Routes, useParams } from "react-router-dom";

import AppShell from "./components/AppShell";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminControlPage from "./pages/AdminControlPage";
import AdminDashboardPage from "./pages/AdminDashboardPage";
import AdminFinancialReportPage from "./pages/AdminFinancialReportPage";
import AdminOperationsPage from "./pages/AdminOperationsPage";
import AdminPaymentReviewPage from "./pages/AdminPaymentReviewPage";
import AdminHistoryPage from "./pages/AdminHistoryPage";
import AdminAuditPage from "./pages/AdminAuditPage";
import AdminReconciliationPage from "./pages/AdminReconciliationPage";
import InvestorHistoryPage from "./pages/InvestorHistoryPage";
import AssetManagerLayout from "./layouts/AssetManagerLayout";
import AssetManagerAssignedProjectsPage from "./pages/AssetManagerAssignedProjectsPage";
import AssetManagerDashboardPage from "./pages/AssetManagerDashboardPage";
import AssetManagerHistoryPage from "./pages/AssetManagerHistoryPage";
import InvestmentsPage from "./pages/InvestmentsPage";
import RedemptionHistoryPage from "./pages/RedemptionHistoryPage";
import TransferableProjectsPage from "./pages/TransferableProjectsPage";
import InvestorDashboardPage from "./pages/InvestorDashboardPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import LoginPage from "./pages/LoginPage";
import NotificationNoticePage from "./pages/NotificationNoticePage";
import NotificationsPage from "./pages/NotificationsPage";
import RegisterPage from "./pages/RegisterPage";
import ShareCertificateVerifyPage from "./pages/ShareCertificateVerifyPage";
import WalletPage from "./pages/WalletPage";
import WalletDepositRequestsPage from "./pages/WalletDepositRequestsPage";
import WalletWithdrawalsPage from "./pages/WalletWithdrawalsPage";
import TransactionsHistoryPage from "./pages/TransactionsHistoryPage";
import MarketingHomePage from "./pages/MarketingHomePage";
import WebsiteSignupPage from "./pages/WebsiteSignupPage";
import ProjectLandingPage from "./pages/ProjectLandingPage";
import KycPage from "./pages/KycPage";
import ProfilePage from "./pages/ProfilePage";
import { useAuth } from "./store/AuthContext";
import { getHomeRouteByRole, resolveStaffHomeRoute } from "./utils/roleRoutes";
import { hasAdminModuleAccess } from "./utils/adminPermissions";

function ShellPage({ children }) {
  return <AppShell>{children}</AppShell>;
}

function RoleHomeRedirect() {
  const { user } = useAuth();
  const staffHome = resolveStaffHomeRoute(user);
  if (staffHome) return <Navigate to={staffHome} replace />;
  return <Navigate to={getHomeRouteByRole(user?.role, user)} replace />;
}

/** Match shareable `/campaign-{account}` links and open the signup form. */
function CampaignReferralRoute() {
  const { campaignSegment } = useParams();
  if (!/^campaign-\d+$/i.test(String(campaignSegment || "").trim())) {
    return <Navigate to="/dashboard" replace />;
  }
  return <RegisterPage />;
}

export default function App() {
  return (
    <Routes>
      <Route path="/" element={<MarketingHomePage />} />
      <Route path="/join" element={<WebsiteSignupPage />} />
      <Route path="/join-our-portfolio" element={<WebsiteSignupPage />} />
      <Route path="/p/:slug" element={<ProjectLandingPage />} />
      <Route
        path="/projects/:projectId/about"
        element={
          <ProtectedRoute>
            <ProjectLandingPage byId />
          </ProtectedRoute>
        }
      />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/signup" element={<RegisterPage />} />
      <Route path="/certificate/verify/:token" element={<ShareCertificateVerifyPage />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <RoleHomeRedirect />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/investor"
        element={
          <ProtectedRoute allowedRoles={["INVESTOR", "MEMBER"]}>
            <ShellPage>
              <InvestorDashboardPage />
            </ShellPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/asset-manager"
        element={
          <ProtectedRoute allowedRoles={["ASSET_MANAGER"]}>
            <ShellPage>
              <AssetManagerLayout />
            </ShellPage>
          </ProtectedRoute>
        }
      >
        <Route index element={<AssetManagerDashboardPage />} />
      </Route>
      <Route
        path="/asset-manager"
        element={
          <ProtectedRoute allowedRoles={["ASSET_MANAGER"]}>
            <ShellPage>
              <AssetManagerLayout />
            </ShellPage>
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/asset-manager/history/pnl" replace />} />
        <Route path="history" element={<Navigate to="/asset-manager/history/pnl" replace />} />
        <Route path="history/:section" element={<AssetManagerHistoryPage />} />
        <Route path="projects" element={<AssetManagerAssignedProjectsPage />} />
      </Route>
      <Route
        path="/dashboard/admin"
        element={
          <ProtectedRoute allowedRoles={["ADMIN", "SUPER_ADMIN", "ACCOUNTANT", "MARKETING"]} requiredAdminModule="DASHBOARD">
            <ShellPage>
              <AdminDashboardPage />
            </ShellPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/financial-report"
        element={
          <ProtectedRoute allowedRoles={["ADMIN", "SUPER_ADMIN", "ACCOUNTANT", "MARKETING"]} requiredAdminModule="DASHBOARD">
            <ShellPage>
              <AdminFinancialReportPage />
            </ShellPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/wallet"
        element={
          <ProtectedRoute allowedRoles={["INVESTOR", "MEMBER"]}>
            <ShellPage>
              <WalletPage />
            </ShellPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/wallet/deposits"
        element={
          <ProtectedRoute allowedRoles={["INVESTOR", "MEMBER"]}>
            <ShellPage>
              <WalletDepositRequestsPage />
            </ShellPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/wallet/withdrawals"
        element={
          <ProtectedRoute allowedRoles={["INVESTOR", "MEMBER"]}>
            <ShellPage>
              <WalletWithdrawalsPage />
            </ShellPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/transactions"
        element={
          <ProtectedRoute allowedRoles={["INVESTOR", "MEMBER"]}>
            <ShellPage>
              <TransactionsHistoryPage />
            </ShellPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ShellPage>
              <ProfilePage />
            </ShellPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/kyc"
        element={
          <ProtectedRoute allowedRoles={["INVESTOR", "MEMBER"]}>
            <ShellPage>
              <KycPage />
            </ShellPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/payments"
        element={
          <ProtectedRoute allowedRoles={["ADMIN", "SUPER_ADMIN", "ACCOUNTANT", "MARKETING"]} requiredAdminModule="PAYMENTS">
            <ShellPage>
              <AdminPaymentReviewPage />
            </ShellPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/control"
        element={
          <ProtectedRoute
            allowedRoles={["ADMIN", "SUPER_ADMIN", "ACCOUNTANT", "MARKETING"]}
            requiredAnyAdminModules={["CONTROL", "OPERATIONS", "PAYMENTS"]}
          >
            <ShellPage>
              <AdminControlPage />
            </ShellPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute allowedRoles={["ADMIN", "SUPER_ADMIN", "ACCOUNTANT", "MARKETING"]} requiredAdminModule="CONTROL">
            <ShellPage>
              <AdminControlPage />
            </ShellPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/investment-projects"
        element={
          <ProtectedRoute allowedRoles={["ADMIN", "SUPER_ADMIN", "ACCOUNTANT", "MARKETING"]} requiredAdminModule="OPERATIONS">
            <Navigate to="/admin/control?tab=PROJECTS" replace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/operations"
        element={
          <ProtectedRoute allowedRoles={["ADMIN", "SUPER_ADMIN", "ACCOUNTANT", "MARKETING"]} requiredAdminModule="OPERATIONS">
            <ShellPage>
              <AdminOperationsPage />
            </ShellPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/history"
        element={
          <ProtectedRoute allowedRoles={["ADMIN", "SUPER_ADMIN", "ACCOUNTANT", "MARKETING"]} requiredAdminModule="HISTORY">
            <ShellPage>
              <Navigate to="/admin/history/deposits" replace />
            </ShellPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/history/:section"
        element={
          <ProtectedRoute allowedRoles={["ADMIN", "SUPER_ADMIN", "ACCOUNTANT", "MARKETING"]} requiredAdminModule="HISTORY">
            <ShellPage>
              <AdminHistoryPage />
            </ShellPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/audit"
        element={
          <ProtectedRoute allowedRoles={["ADMIN", "SUPER_ADMIN", "ACCOUNTANT", "MARKETING"]} requiredAdminModule="HISTORY">
            <ShellPage>
              <AdminAuditPage />
            </ShellPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/reconciliation/*"
        element={
          <ProtectedRoute allowedRoles={["ADMIN", "SUPER_ADMIN", "ACCOUNTANT"]} requiredAdminModule="OPERATIONS">
            <ShellPage>
              <AdminReconciliationPage />
            </ShellPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/investments"
        element={
          <ProtectedRoute allowedRoles={["INVESTOR", "MEMBER", "ADMIN", "SUPER_ADMIN"]}>
            <ShellPage>
              <InvestmentsPage />
            </ShellPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/investments/transfers"
        element={
          <ProtectedRoute allowedRoles={["INVESTOR", "MEMBER"]}>
            <ShellPage>
              <TransferableProjectsPage />
            </ShellPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/investments/redemptions"
        element={
          <ProtectedRoute allowedRoles={["INVESTOR", "MEMBER"]}>
            <ShellPage>
              <RedemptionHistoryPage />
            </ShellPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-history"
        element={
          <ProtectedRoute allowedRoles={["INVESTOR", "MEMBER"]}>
            <ShellPage>
              <Navigate to="/my-history/deposits" replace />
            </ShellPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-history/:section"
        element={
          <ProtectedRoute allowedRoles={["INVESTOR", "MEMBER"]}>
            <ShellPage>
              <InvestorHistoryPage />
            </ShellPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications/:noticeId"
        element={
          <ProtectedRoute>
            <ShellPage>
              <NotificationNoticePage />
            </ShellPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <ShellPage>
              <NotificationsPage />
            </ShellPage>
          </ProtectedRoute>
        }
      />
      {/*
        Campaign links are `{domain}/campaign-{account}` (one path segment).
        React Router cannot match `/campaign-:id` inside a segment, so this
        single-segment catch handles them before the generic redirect.
      */}
      <Route path="/:campaignSegment" element={<CampaignReferralRoute />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}
